import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, GlobalStyles, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import AndroidRoundedIcon from "@mui/icons-material/AndroidRounded";
import AppleIcon from "@mui/icons-material/Apple";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { api, publicApi } from "../apiClient";
import { APP_FOOTER_TEXT, APP_LOGIN_LOGO_TEXT } from "../config";
import { t } from "../i18n";
import oceanLogo from "../assets/ocean-logo.png";
import { uiColors } from "../uiStyles";

export default function SharedProfilePage({ token }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loginTheme, setLoginTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return window.__OCEAN_LOGIN_THEME__ || localStorage.getItem("ocean_login_theme") || "light";
  });
  const [loginLocale, setLoginLocale] = useState(() => {
    if (typeof window === "undefined") return "ru";
    return window.__OCEAN_LOGIN_LOCALE__ || localStorage.getItem("ocean_login_locale") || "ru";
  });
  const [tick, setTick] = useState(0);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/login/settings").then((response) => {
      if (!mounted) return;
      const next = response.data?.settings || {};
      const nextTheme = next.theme === "dark" ? "dark" : next.theme === "system" ? "system" : "light";
      const nextLocale = next.locale === "en" ? "en" : "ru";
      setLoginTheme(nextTheme);
      setLoginLocale(nextLocale);
      if (typeof window !== "undefined") {
        window.__OCEAN_LOGIN_THEME__ = nextTheme;
        window.__OCEAN_LOGIN_LOCALE__ = nextLocale;
        localStorage.setItem("ocean_login_theme", nextTheme);
        localStorage.setItem("ocean_login_locale", nextLocale);
      }
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    publicApi.get(`/share/profile/${encodeURIComponent(token)}`).then((response) => {
      if (!mounted) return;
      setPayload(response.data?.item || null);
      setError("");
    }).catch((err) => {
      if (!mounted) return;
      setError(err.response?.data?.error || "Link not found");
    }).finally(() => {
      if (mounted) setLoading(false);
    });
    return () => { mounted = false; };
  }, [token]);

  useEffect(() => {
    if (!payload?.expiresAt) return undefined;
    const id = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(id);
  }, [payload?.expiresAt]);

  const getSystemTheme = () => (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
  const activeLocale = loginLocale || "ru";
  const resolvedLoginTheme = loginTheme === "system" ? getSystemTheme() : loginTheme;
  const isDark = resolvedLoginTheme === "dark";

  const shellBg = useMemo(() => (isDark
    ? "radial-gradient(circle at top, #252525 0%, #171717 55%, #121212 100%)"
    : "linear-gradient(180deg, #f4f5f7 0%, #edeef1 100%)"), [isDark]);

  const remainingLabel = useMemo(() => {
    if (!payload?.expiresAt) return "";
    const diffMs = new Date(payload.expiresAt).getTime() - Date.now();
    if (diffMs <= 0) return t("shell.shareExpired", activeLocale);
    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (activeLocale === "ru") {
      if (days > 0) return `${days} д ${hours} ч ${minutes} мин`;
      if (hours > 0) return `${hours} ч ${minutes} мин ${seconds} сек`;
      return `${minutes} мин ${seconds} сек`;
    }
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  }, [payload?.expiresAt, activeLocale, tick]);

  const cardBg = isDark ? "rgba(24,24,24,0.96)" : "rgba(255,255,255,0.96)";
  const cardBorder = isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(17,24,39,0.08)";
  const profileFileName = "ocean.conf";
  const copy = activeLocale === "ru" ? {
    download: "Скачать профиль",
    installTitle: "Установить приложение",
    appStore: "App Store",
    googlePlay: "Google Play",
    instructions: "Как подключить",
    dialogTitle: "Как подключить профиль",
    commonTitle: "Сначала",
    commonSteps: [
      <>Установите приложение <strong>AmneziaWG</strong> для своего устройства, нажав соответствующую иконку на этой странице.</>,
      <>Скачайте файл профиля подключения <strong>ocean.conf</strong>.</>,
      <>При первом запуске разрешите приложению добавить VPN-конфигурацию на устройство.</>,
    ],
    iosTitle: "На iPhone",
    iosSteps: [
      <>Откройте скачанный файл <strong>ocean.conf</strong> в приложении <strong>«Файлы»</strong>.</>,
      <>Нажмите <strong>«Поделиться»</strong>.</>,
      <>В списке приложений выберите <strong>AmneziaWG</strong>. Если приложение не отображается, нажмите <strong>«Ещё»</strong> и выберите его там.</>,
      <>Подтвердите импорт профиля.</>,
      <>Включите VPN с помощью переключателя рядом с профилем <strong>ocean</strong>.</>,
    ],
    androidTitle: "На Android",
    androidSteps: [
      <>Откройте приложение <strong>AmneziaWG</strong> и нажмите кнопку <strong>«+»</strong>.</>,
      <>Выберите <strong>«Импорт из файла или архива»</strong>.</>,
      <>Откройте папку <strong>«Загрузки»</strong> и выберите скачанный файл <strong>ocean.conf</strong>.</>,
      <>Разрешите приложению создать VPN-подключение.</>,
      <>Включите VPN с помощью переключателя рядом с профилем <strong>ocean</strong>.</>,
    ],
    close: "Понятно",
  } : {
    download: "Download profile",
    installTitle: "Install application",
    appStore: "App Store",
    googlePlay: "Google Play",
    instructions: "How to connect",
    dialogTitle: "How to connect the profile",
    commonTitle: "First",
    commonSteps: [
      <>Install <strong>AmneziaWG</strong> for your device by tapping the appropriate icon on this page.</>,
      <>Download the <strong>ocean.conf</strong> connection profile file.</>,
      <>On first launch, allow the app to add a VPN configuration to your device.</>,
    ],
    iosTitle: "On iPhone",
    iosSteps: [
      <>Open the downloaded <strong>ocean.conf</strong> file in the <strong>Files</strong> app.</>,
      <>Tap <strong>Share</strong>.</>,
      <>Choose <strong>AmneziaWG</strong> from the app list. If it is not shown, tap <strong>More</strong> and select it there.</>,
      <>Confirm the profile import.</>,
      <>Enable VPN using the toggle next to the <strong>ocean</strong> profile.</>,
    ],
    androidTitle: "On Android",
    androidSteps: [
      <>Open <strong>AmneziaWG</strong> and tap <strong>+</strong>.</>,
      <>Choose <strong>Import from file or archive</strong>.</>,
      <>Open the <strong>Downloads</strong> folder and select the downloaded <strong>ocean.conf</strong> file.</>,
      <>Allow the app to create a VPN connection.</>,
      <>Enable VPN using the toggle next to the <strong>ocean</strong> profile.</>,
    ],
    close: "Got it",
  };

  const handleDownload = async () => {
    const directUrl = `${window.location.origin}/api/share/profile/${encodeURIComponent(token)}/download`;
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
    if (isMobile) {
      window.location.href = directUrl;
      return;
    }
    try {
      const response = await publicApi.get(`/share/profile/${encodeURIComponent(token)}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/octet-stream" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", profileFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.location.href = directUrl;
    }
  };

  return (
    <>
      <GlobalStyles styles={{ body: { margin: 0, background: shellBg } }} />
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", px: 2, py: 4 }}>
        <Paper elevation={0} sx={{ width: "100%", maxWidth: 420, p: 3.5, borderRadius: 5, backgroundColor: cardBg, backgroundImage: "none", border: cardBorder, boxShadow: isDark ? "0 20px 40px rgba(0,0,0,0.45)" : "0 20px 45px rgba(15,23,42,0.10)" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2.5 }}>
            <Box component="img" src={oceanLogo} alt="Logo" sx={{ width: 120, height: "auto", mb: 0.75, filter: isDark ? "drop-shadow(0 6px 18px rgba(0,0,0,0.34))" : "drop-shadow(0 6px 16px rgba(15,23,42,0.10))" }} />
            <Typography align="center" gutterBottom sx={{ m: 0, fontFamily: "RodondoRUS, sans-serif", fontSize: 24, fontWeight: "bold", color: isDark ? "#f8fafc" : "#111827" }}>{APP_LOGIN_LOGO_TEXT}</Typography>
          </Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} sx={{ color: isDark ? "#cbd5e1" : uiColors.primary }} /></Box>
          ) : error ? (
            <Typography align="center" sx={{ color: isDark ? "#fda4af" : "#b91c1c", py: 2 }}>{error}</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <Typography align="center" sx={{ fontWeight: 600, color: isDark ? "#f8fafc" : "#111827" }}>{payload?.displayName || payload?.profileName}</Typography>
              <Box sx={{ width: "100%", textAlign: "center" }}>
                <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 700, color: isDark ? "rgba(248,250,252,0.88)" : "rgba(17,24,39,0.82)" }}>{copy.installTitle}</Typography>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}>
                  <Tooltip title={copy.appStore} arrow>
                    <IconButton component="a" href="https://apps.apple.com/app/amneziawg/id6478942365" target="_blank" rel="noopener noreferrer" aria-label={copy.appStore} sx={{ width: 54, height: 54, border: isDark ? "1px solid rgba(148,163,184,0.28)" : "1px solid rgba(17,24,39,0.14)", color: isDark ? "#f8fafc" : "#111827", backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(17,24,39,0.025)", "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(17,24,39,0.07)" } }}><AppleIcon sx={{ fontSize: 25 }} /></IconButton>
                  </Tooltip>
                  <Tooltip title={copy.googlePlay} arrow>
                    <IconButton component="a" href="https://play.google.com/store/apps/details?id=org.amnezia.awg" target="_blank" rel="noopener noreferrer" aria-label={copy.googlePlay} sx={{ width: 54, height: 54, border: isDark ? "1px solid rgba(148,163,184,0.28)" : "1px solid rgba(17,24,39,0.14)", color: isDark ? "#f8fafc" : "#111827", backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(17,24,39,0.025)", "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(17,24,39,0.07)" } }}><AndroidRoundedIcon sx={{ fontSize: 25 }} /></IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ width: "100%", pt: 1.5, textAlign: "center" }}>
                <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 700, color: isDark ? "rgba(248,250,252,0.88)" : "rgba(17,24,39,0.82)" }}>{copy.download}</Typography>
                <Tooltip title={copy.download} arrow>
                  <IconButton onClick={handleDownload} aria-label={copy.download} sx={{ width: 58, height: 58, border: isDark ? "1px solid rgba(148,163,184,0.28)" : "1px solid rgba(17,24,39,0.14)", color: isDark ? "#f8fafc" : "#111827", backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(17,24,39,0.025)", "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(17,24,39,0.07)" } }}>
                    <DownloadRoundedIcon sx={{ fontSize: 26 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Button fullWidth variant="text" startIcon={<HelpOutlineRoundedIcon />} onClick={() => setInstructionsOpen(true)} sx={{ minHeight: 42, borderRadius: 3, textTransform: "none", color: isDark ? "#cbd5e1" : uiColors.primary }}>
                {copy.instructions}
              </Button>
              <Tooltip title={t("shell.shareExpiresIn", activeLocale).replace("{time}", remainingLabel)} arrow>
                <Typography align="center" variant="caption" sx={{ color: isDark ? "rgba(226,232,240,0.50)" : "rgba(17,24,39,0.46)", cursor: "default" }}>{remainingLabel}</Typography>
              </Tooltip>
            </Box>
          )}
          <Typography align="center" sx={{ mt: 3, color: isDark ? "rgba(226,232,240,0.46)" : "rgba(17,24,39,0.44)", fontSize: 12 }}>{APP_FOOTER_TEXT}</Typography>
        </Paper>
      </Box>
      <Dialog open={instructionsOpen} onClose={() => setInstructionsOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { m: { xs: 1.5, sm: 3 }, width: { xs: "calc(100% - 24px)", sm: "100%" }, maxHeight: { xs: "calc(100% - 24px)", sm: "calc(100% - 64px)" }, borderRadius: { xs: 3, sm: 4 }, backgroundImage: "none", backgroundColor: cardBg, border: cardBorder } }}>
        <DialogTitle sx={{ pr: 3, fontWeight: 800, color: isDark ? "#f8fafc" : "#111827" }}>{copy.dialogTitle}</DialogTitle>
        <DialogContent dividers sx={{ borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(17,24,39,0.08)" }}>
          {[[copy.commonTitle, copy.commonSteps], [copy.iosTitle, copy.iosSteps], [copy.androidTitle, copy.androidSteps]].map(([title, steps]) => (
            <Box key={title} sx={{ mb: 2.5, "&:last-child": { mb: 0 } }}>
              <Typography sx={{ mb: 0.75, fontWeight: 800, color: isDark ? "#f8fafc" : "#111827" }}>{title}</Typography>
              <Box component="ul" sx={{ m: 0, py: 0, pr: 0, pl: 3, listStyle: "none", color: isDark ? "rgba(226,232,240,0.82)" : "rgba(17,24,39,0.78)", "& li": { mb: 0.75, lineHeight: 1.45 } }}>
                {steps.map((step, index) => <li key={`${title}-${index}`}><Typography component="span" sx={{ fontSize: 14 }}>{step}</Typography></li>)}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button fullWidth variant="contained" onClick={() => setInstructionsOpen(false)} sx={{ minHeight: 44, borderRadius: 3, textTransform: "none", fontWeight: 700, color: "#fff", boxShadow: "none", backgroundColor: isDark ? "#7a746b" : "#6b7280", "&:hover": { backgroundColor: isDark ? "#625d56" : "#4b5563", boxShadow: "none" } }}>{copy.close}</Button></DialogActions>
      </Dialog>
    </>
  );
}
